import { getAllBackorders, getAllPurchaseOrderDrafts, getAllPurchaseOrders, getAllSupplierProducts, getAllSuppliers, getLowOrderPoints, getLowOrderPointsById, getLowOrderPointsReferenceData, getProductPurchaseHistory, getPurchaseOrderById, getPurchaseOrderDraftById, getPurchaseOrderReferenceData, getPurchaseOrders, getSupplierById, getSupplierProductById, getSupplierProductBySku, getSupplierProductsBySupplierId, getSuppliers } from "src/core/api/apiRequests";
import { useGetAllPaginatedItems } from "src/core/api/useGetAllPaginatedItems";
import useResponse from "src/core/api/useResponse";
import type { KeyValuePair } from 'src/core/api/useResponse';

export const useSuppliers = () => {
    const { rows, status } = useResponse<any[]>({
        requestFunction: getSuppliers,
        values: null,
    });
    return { rows, status };
};

export const useGetAllSuppliers = () => {
    const { rows, status } = useGetAllPaginatedItems<Supplier>({
        requestFunction: getAllSuppliers,
        pageSize: 100,
        idField: "Id",
        requestValues: null,
        shouldRequest: true,
        incremental: { enabled: true },
    });
    return { rows, status };
};

export const useSuppliersById = (id: number) => {
    const { rows: response } = useResponse<Supplier[]>({
        requestFunction: getSupplierById,
        values: { id },
        requestNothingOnNull: true,
        dependency: id,
    });
    return response;
};

export const useSupplierDefaultsById = (id: string | number) => {
    return useResponse<any>({
        requestFunction: getSupplierById,
        values: { id },
        requestNothingOnNull: true,
        dependency: id,
    });
};

export const useGetAllSupplierProducts = () => {
    return useGetAllPaginatedItems<SupplierProduct>({
        requestFunction: getAllSupplierProducts,
        pageSize: 1000,
        idField: "SupplierAssortmentItemId",
    });
};

export const useGetSupplierProductById = (id: number, dependecy = null) => {
    const { rows: response } = useResponse<SupplierProduct>({
        requestFunction: getSupplierProductById,
        values: { id },
        requestNothingOnNull: true,
        dependency: dependecy,
    });
    //console.log("Supplier Product", response)
    return response;
};

export const useGetSupplierProductsByProductId = (id: number, deps?) => {
    const { rows: response } = useResponse<SupplierProduct[]>({
        requestFunction: getSupplierProductBySku,
        values: { id },
        requestNothingOnNull: true,
        dependency: deps,
    });
    return response;
};

export const usePurchaseOrders = () => {
    const { rows: response } = useResponse<any[]>({
        requestFunction: getPurchaseOrders,
    });
    return response;
};

export const useGetAllPurchaseOrders = (shouldRequest = true, statusFilter = null) => {
    const { rows, status } = useGetAllPaginatedItems<any>({
        requestFunction: getAllPurchaseOrders,
        pageSize: 500,
        idField: "PurchaseOrderNo",
        requestValues: statusFilter,
        shouldRequest,
    });
    return { rows, status };
};

export const useGetActivePurchaseOrders = (shouldRequest = true) => {
    return useGetAllPurchaseOrders(shouldRequest, "28,29,30,31,32,33"); //Created, Changed, Sent, Confirmed, Shipped, In Progress
};

export const useGetInactivePurchaseOrders = (shouldRequest = true) => {
    return useGetAllPurchaseOrders(shouldRequest, "34,35,36"); //Completed, Received Short, Cancelled
};

export const useLatestPurchaseOrders = (amount = 5) => {
    const { rows, status } = useResponse<any[]>({
        requestFunction: getAllPurchaseOrders,
        values: { amount, pageSize: 1 },
        requestNothingOnNull: true,
    });
    return rows;
};

export const useGetAllPurchaseOrderDrafts = () => {
    return useGetAllPaginatedItems<any>({
        requestFunction: getAllPurchaseOrderDrafts,
        pageSize: 500,
        idField: "PurchaseOrderDraftId",
    });
};

export const useGetAllBackorders = () => {
    return useGetAllPaginatedItems<any>({
        requestFunction: getAllBackorders,
        pageSize: 500,
        idField: "ArticleNumber",
    });
};

export const useGetPurchaseOrderReferenceData = () => {
    const { rows, setNewValue, abort } = useResponse<any>({
        requestFunction: getPurchaseOrderReferenceData,
    });
    return [rows, setNewValue, abort] as [ typeof rows, typeof setNewValue, typeof abort ];
};

export const usePurchaseOrderById = (id: string, dependency = null) => {
    const { rows } = useResponse<any>({
        requestFunction: getPurchaseOrderById,
        values: { id },
        requestNothingOnNull: true,
        dependency: [id, ...(Array.isArray(dependency) ? dependency : [dependency])],
    });
    return rows;
};

export const useProductPurchaseHistory = (productId) => {
    const { rows: result } = useResponse<any[]>({
        requestFunction: getProductPurchaseHistory,
        values: { productId },
        requestNothingOnNull: true,
        dependency: productId,
    });
    // console.log("Purchase History", result, productId);
    return result;
};

export const useGetSupplierProductsBySupplierId = (supplierId: string | number) => {
    const { rows, status } = useGetAllPaginatedItems<any>({
        requestFunction: getSupplierProductsBySupplierId,
        pageSize: 500,
        idField: "SupplierAssortmentItemId",
        requestValues: supplierId,
        shouldRequest: supplierId != null && supplierId !== '',
    });
    return { rows, status };
};

export const usePurchaseOrderDraftById = (id: string, dependency = null) => {
    const { rows } = useResponse<any>({
        requestFunction: getPurchaseOrderDraftById,
        values: { id },
        requestNothingOnNull: true,
        dependency: [id, ...(Array.isArray(dependency) ? dependency : [dependency])],
    });
    return rows;
};

export const useLowOrderPoints = () => {
    return useResponse<any[]>({
        requestFunction: getLowOrderPoints,
    });
};

export const useLowOrderPointsById = (id: string) => {
    const { rows: response } = useResponse<any>({
        requestFunction: getLowOrderPointsById,
        values: { id },
        requestNothingOnNull: true,
        dependency: id,
    });
    return response;
};

export const useLowOrderPointsReferenceData = () => {
    const { rows, setNewValue, abort } = useResponse<any>({
        requestFunction: getLowOrderPointsReferenceData,
    });
    return [rows, setNewValue, abort] as [ typeof rows, typeof setNewValue, typeof abort ];
};

export interface Supplier {
    Id: number;
    Supplier?: KeyValuePair;
    Name: string;
    Contact: string;
    Phone: string;
    Mobile: string;
    Disabled: boolean;
    ShortName: string;
    DropShipEnabled: boolean;
    Country: string;
}

interface SupplierProduct {
    Id: number;
    ProductsId: string;
    ProductName: string;
    VendorAssortmentName: string;
    VendorName: string;
    ManufacturerId: number;
    VendorProductNo: string;
    VendorProductName: string;
    Price?: number;
    CurrencyConvertedPrice?: number;
    TARIC?: number;
    VendorAssortmentsId: number;
    ManufacturerName: string;
    ManufacturerProductNo: string;
    ManufacturerProductName: string;
    CurrencyId: number;
    Currency: string;
    OrganizationCurrencyId: number;
    OrganizationCurrency: string;
    CreateDate: Date;
    ModifyDate: Date;
    ValidFrom: Date;
    ValidTo?: Date;
    ETA?: Date;
    UpdatedAt?: Date;
    Quantity: string;
    Status: string;
    Internal: boolean;
    ProductType: string;
    QuantityInStock: number;
    QuantityOnOrder: number;
    EAN: string;
}
